<script>
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Button from '@/components/Button';

dayjs.extend(customParseFormat);

export default {
  name: 'LaunchPadCard',
  components: { Button },
  props: {
    product: Object,
    link: String,
  },
  computed: {
    status() {
      if (this.isLive) {
        return this.$t('upcoming.live');
      }
      return dayjs(this.product.date, 'MMMM DD').isValid()
        && dayjs().isAfter(dayjs(this.product.date, 'MMMM DD'), 'days')
        ? this.$t('upcoming.closed')
        : this.$t('upcoming.upcoming');
    },
    isUpcoming() {
      return !dayjs(this.product.date, 'MMMM DD').isValid()
        ? false
        : dayjs().isBefore(dayjs(this.product.date, 'MMMM DD'), 'days');
    },
    isLive() {
      return !dayjs(this.product.date, 'MMMM DD').isValid()
        ? false
        : dayjs().isSame(dayjs(this.product.date, 'MMMM DD'), 'days');
    },
    calendarLink() {
      if (dayjs(this.product.date, 'MMMM DD').isValid()) {
        const dateFormat = dayjs(this.product.date, 'MMMM DD').format('YYYYMMDD');
        const timeStartArr = String(this.product.time === 'TBA' ? '07:00' : this.product.time)
          .toLowerCase()
          .replace(/[a-z|\s]/g, '')
          .split(':');
        const timeStart = timeStartArr
          .map((i) => (i.length < 2 ? `0${i}` : i))
          .join('')
          .slice(0, 4);
        const params = {
          action: 'TEMPLATE',
          text: this.product.name,
          dates: `${dateFormat}T${timeStart}00Z/${dateFormat}T${timeStart}00Z`,
        };
        const paramsEncoded = new URLSearchParams(params).toString();
        return `https://www.google.com/calendar/render?${paramsEncoded}`;
      }
      return '#';
    },
  },
  methods: {
    getIconSrc() {
      // eslint-disable-next-line global-require,import/no-dynamic-require
      return require(`../../assets/img/${this.$chain.getName()}/launchpad/${
        this.product.icon
      }.jpg`);
    },
    getEthIcon() {
      // eslint-disable-next-line global-require,import/no-dynamic-require
      return require(`@/assets/img/${this.$chain.getName()}/launchpad/${
        this.product.paymentTokenIcon
      }.png`);
    },
    getCalIcon() {
      // eslint-disable-next-line global-require,import/no-dynamic-require
      return require(`@/assets/img/${this.$chain.getName()}/ic-calendar.svg`);
    },
  },
};
</script>

<template>
  <v-col md="4" sm="6" cols="12">
    <div class="launchpad-card main-block">
      <div class="launchpad-card__header">
        <div class="launchpad-card__status">
          <span class="launchpad-card__step" :class="{ 'launchpad-card__step--live': isLive }">{{
            status
          }}</span>
          <div class="launchpad-card__currency d-flex justify-center align-center">
            <img :src="getEthIcon()" />
          </div>
        </div>
        <div class="launchpad-card__icon d-flex justify-center align-center">
          <img :src="getIconSrc()" />
          <div v-if="product.saleType === 'INO'" class="launchpad-card__title-subline">
            {{ product.saleType }}
          </div>
        </div>
        <div class="launchpad-card__desc">
          <div class="text-center">
            <h3 class="launchpad-card__title" v-html="product.name"></h3>
            <div class="launchpad-card__btn-wrapper">
              <Button class="launchpad-card__date" :to="`/tokensale/${link}`">
                <span>{{ product.date === 'TBA' ? 'Date - ' : '' }}{{ product.date }}</span>
              </Button>
              <Button
                :href="calendarLink"
                class="launchpad-card__calendar nft-link"
                v-if="isUpcoming"
              >
                <img
                  :src="getCalIcon()"
                  alt="calendar"
                  class="launchpad-card__calendar-icon"
                /><span>Add to calendar</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div class="launchpad-card__table">
        <div class="launchpad-card__table--row">
          <span>{{ $t('launchpad.card.token-ticker') }}</span>
          <strong>{{ product.tokenTicker }}</strong>
        </div>
        <div class="launchpad-card__table--row">
          <span>{{ $t('launchpad.card.total-raise') }}</span>
          <strong>{{ product.totalRaise }}</strong>
        </div>
        <div class="launchpad-card__table--row">
          <span>{{ $t('launchpad.card.price') }}</span>
          <strong>{{ product.price }}</strong>
        </div>
      </div>
    </div>
  </v-col>
</template>

<style lang="scss">
.launchpad-card {
  text-decoration: none;
  color: #283287;
  font-size: 1rem;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3.6rem 3.3rem 0rem 3.3rem;

  body.binance & {
    color: #fff;
  }

  @media screen and (max-width: $breakpoint-sm) {
    padding: 2rem;
  }

  &__currency {
    height: 4.5rem;
    width: 4.5rem;

    background: $btn-bg;
    border-radius: 50%;
    box-shadow: $pools-select-shadow;

    body.binance & {
      color: $b-secondatry-font-color;
      background: $b-main-bg;
      box-shadow: $b-pools-select-shadow;
    }

    img {
      height: 3.5rem;
      width: 3.5rem;
    }
  }
  &__step {
    position: relative;
    padding: 1rem 3rem 1rem 3.2rem;
    display: flex;
    font-size: 1.4rem;
    font-weight: 600;

    color: #ff9d9d;
    background: #ffffff;
    box-shadow: $pools-select-shadow;
    border-radius: 5rem;

    body.binance & {
      color: $b-secondatry-font-color;
      background: $b-main-bg;
      box-shadow: $b-pools-select-shadow;
    }

    &::before {
      display: block;
      content: '';
      position: absolute;
      width: 0.8rem;
      height: 0.8rem;
      left: 1.5rem;
      top: 1.7rem;
      background: $main-pink;
      border-radius: 50%;
    }

    &--live {
      &::before {
        background: $main-green;
      }
    }

    &--sm {
      padding: 0.7rem 1.3rem 0.7rem 2rem;
      margin-top: 0.5rem;
      width: 6.7rem;

      font-size: 1rem;

      &::before {
        width: 0.4rem;
        height: 0.4rem;
        left: 0.8rem;
        top: 1.2rem;
      }
    }
  }

  &__status {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .v-progress-linear {
    border-radius: 50px;
    @media screen and (max-width: $breakpoint-sm) {
      height: 3px !important;
    }
  }

  &__header {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  &__progress {
    width: 100%;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    margin-top: 1.8rem;

    .progress {
      color: #7478a0;
      font-weight: 600;
      font-size: 1.8rem;
      margin-bottom: 0.4rem;

      body.binance & {
        color: $b-secondatry-font-color;
      }
    }

    &--footer {
      display: flex;
      justify-content: space-between;
      margin-top: 1.2rem;
      font-size: 1.6rem;
      font-weight: 600;
      padding-bottom: 2.3rem;
      border-bottom: 2px solid rgba(160, 165, 213, 0.3);

      @media screen and (max-width: $breakpoint-sm) {
        border-bottom-width: 1px;
      }

      strong {
        color: #ff7979;
        font-weight: 600;

        body.binance & {
          color: $b-binance-color;
        }
      }

      span:first-child {
        color: #7478a0;

        body.binance & {
          color: $b-secondatry-font-color;
        }
      }
    }

    &--bar {
      background: #f6e7e2;
      border-radius: 3px;
      height: 0.8rem;

      div {
        background: $main-pink;
        border-radius: 3px;
        height: 0.8rem;
      }
    }
  }

  &__price {
    font-size: 1.6rem;
    color: #283287;
    font-weight: 600;

    body.binance & {
      color: $b-secondatry-font-color;
    }

    @media screen and (max-width: $breakpoint-sm) {
      font-size: 1rem;
    }
  }

  &__title {
    font-size: 3.6rem;
    z-index: 2;
    position: relative;
    width: min-content;
    margin: 0 auto;

    @media screen and (max-width: $breakpoint-sm) {
      font-size: 1.8rem;
    }

    &-subline {
      font-size: 1.5rem;
      position: absolute;
      right: 4rem;
      top: 21rem;
    }
  }

  &__date,
  &__calendar {
    max-width: 24rem;
    padding: 1.3rem 1.8rem;
    font-size: 1.3rem;
    text-transform: none;

    @media screen and (max-width: $breakpoint-sm) {
      height: 2.8rem;
      padding: 1rem 2.5rem;
      font-size: 1rem;
    }

    &-icon {
      height: 1em;
      margin-right: 0.5em;
      vertical-align: bottom;
    }
  }

  &__date {
    padding: 1.3rem 2.8rem 1.3rem 3.8rem;

    &:before {
      content: '•';
      position: absolute;
      font-size: 22px;
      margin-left: -16px;
      margin-top: -1px;
    }

    &:after {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      content: '';
    }
  }

  &__calendar {
    z-index: 2;
    position: relative;
  }

  &__table {
    margin-top: 2.2rem;
    width: 100%;
    font-size: 1.8rem;
    margin-bottom: 4.2rem;

    @media screen and (max-width: $breakpoint-sm) {
      margin-bottom: 0;
      margin-top: 0.8rem;

      font-size: 1.4rem;
    }

    &--row {
      margin-top: 1rem;
      display: flex;
      justify-content: space-between;

      @media screen and (max-width: $breakpoint-sm) {
        margin-top: 1.5rem;
      }

      span {
        color: $grey-font-color;
        font-weight: 600;

        body.binance & {
          color: $b-grey-font-color;
        }
      }

      strong {
        color: #343e9a;

        body.binance & {
          color: #fff;
        }
      }
    }
  }

  &__icon {
    background: $btn-bg;
    box-shadow: $btn-shadow;
    border-radius: 50%;
    margin-bottom: 1rem;
    width: 13.5rem;
    height: 13.5rem;

    body.binance & {
      color: $b-secondatry-font-color;
      background: $b-main-bg;
      box-shadow: $b-pools-select-shadow;
    }

    @media screen and (max-width: $breakpoint-sm) {
      width: 7rem;
      height: 7rem;
      margin-right: 1.5rem;
    }

    img {
      width: 10.5rem;
      height: 10.5rem;
      border-radius: 50%;

      @media screen and (max-width: $breakpoint-sm) {
        width: 5rem;
        height: 5rem;
      }
    }
  }

  &__btn-wrapper {
    margin-top: 1rem;
    display: inline-flex;
    flex-wrap: wrap;
    gap: 1.8rem;
    justify-content: center;
  }
}
</style>
